"use client"

import { useState, useRef, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  getWhatsappNoRegisteredAction,
  saveWhatsappContactAction,
  sendWhatsappTempletesAction,
  uploadImageAction,
} from "../../../services/action/common"
import { Search, Filter, MoreHorizontal, Check, FileSpreadsheet, Download, X } from "lucide-react"
import * as XLSX from "xlsx"
import ContactModals from "./contact-modals"
import { getAllUsersAction } from "../../../services/action/manager"
import debounce from "lodash/debounce"
import InfiniteScroll from "react-infinite-scroll-component"

// Reusable contact card component to avoid duplication
const ContactCard = ({
  dropdownOpen,
  setDropdownOpen,
  selectedContacts,
  handleSelectContact,
  contact,
  isMobile = false,
  index,
}) => {
  return (
    <div
      className={`flex items-center ${
        isMobile ? "space-x-3 rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-all" : ""
      }`}
    >
      {isMobile && <div className="flex-shrink-0 w-8 text-xs text-gray-500 font-medium text-center">{index + 1}</div>}
      <div className="flex items-center justify-center">
        <div
          className="flex items-center justify-center"
          style={{ minWidth: "30px", minHeight: "30px" }} // Larger touch target
        >
          <div
            onClick={(e) => {
              e.preventDefault() // Prevent any default behavior
              e.stopPropagation() // Stop event bubbling
              handleSelectContact(contact.id)
            }}
            onTouchEnd={(e) => {
              // Add specific touch event for mobile
              e.preventDefault()
              e.stopPropagation()
              handleSelectContact(contact.id)
            }}
            className={`w-4 h-4 border border-gray-300 rounded flex items-center justify-center cursor-pointer transition-colors ${
              selectedContacts.includes(contact.id) ? "bg-[#001B3D] border-[#001B3D]" : "bg-white hover:bg-gray-100"
            }`}
          >
            {selectedContacts.includes(contact.id) && <Check className="w-3 h-3 text-white" />}
          </div>
        </div>
      </div>
      <div className={`${isMobile ? "flex-1 min-w-0" : ""}`}>
        <p
          className={`${
            isMobile ? "font-medium truncate" : "whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900"
          }`}
        >
          {contact.name}
        </p>
        {isMobile && (
          <>
            <p className="text-sm text-gray-500 truncate">{contact.number}</p>
            <p className="text-sm text-gray-500 truncate">{contact.email}</p>
            <div className="flex items-center mt-1 space-x-2">
              <span className="inline-flex items-center rounded-full bg-[#001B3D] bg-opacity-10 px-2 py-0.5 text-xs font-medium text-[#001B3D]">
                {contact.tag}
              </span>
              <span className="text-xs text-gray-500">{contact.source}</span>
            </div>
          </>
        )}
      </div>
      <div className="relative">
        <button
          onClick={() => setDropdownOpen(dropdownOpen === contact.id ? null : contact.id)}
          className="text-gray-400 hover:text-gray-500 transition-colors"
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>

        {dropdownOpen === contact.id && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 animate-[fadeIn_0.2s_ease-in-out]">
            <div className="py-1">
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setDropdownOpen(null)
                }}
              >
                Edit
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setDropdownOpen(null)
                }}
              >
                Message
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setDropdownOpen(null)
                }}
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function ContactsManagement({
  showToast,
  templeteList,
  selectedNewNumber,
  setSelectedNewNumber,
  showChangeMobileModal,
  setShowChangeMobileModal,
  getTemplete,
}) {
  const { userList } = useSelector((state) => state.managerReducer)
  const { user } = useSelector((state) => state.authenticationReducer)

  // Transform userList to match the expected contact format
  const contacts =
    userList?.data?.map((user) => ({
      id: user._id,
      name: user.name,
      number: user.countryCode + " " + user.number,
      tag: user.tag || user.userType || "CTWA Lead",
      source: user.source || "AD",
      email: user.email,
      registerType: user.registerType || "contact",
    })) || []

  const [selectedContacts, setSelectedContacts] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [showAddContactModal, setShowAddContactModal] = useState(false)
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(null)
  const [importDropdownOpen, setImportDropdownOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [showBroadcastTemplateModal, setShowBroadcastTemplateModal] = useState(false)
  const [broadcastStep, setBroadcastStep] = useState(1) // 1: Select template, 2: Fill variables
  const [templateVariables, setTemplateVariables] = useState({})
  const [variableInputs, setVariableInputs] = useState({})
  const [autoFillOptions, setAutoFillOptions] = useState({})

  const [pageData, setPageData] = useState({
    page: 1,
    limit: 15,
  })

  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Add a new state for image upload
  const [headerImage, setHeaderImage] = useState(null)
  const fileInputRef = useRef(null)
  const excelFileInputRef = useRef(null)
  const [attachments, setAttachments] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [newContact, setNewContact] = useState({
    name: "",
    number: "",
    tag: "",
    email: "",
    registerType: "contact",
  })
  const [errors, setErrors] = useState({})
  // Add a new state to track when broadcasting is in progress
  const [isBroadcasting, setIsBroadcasting] = useState(false)
  // Add state for Excel import
  const [isImporting, setIsImporting] = useState(false)
  const [importedContacts, setImportedContacts] = useState([])
  const [showImportPreviewModal, setShowImportPreviewModal] = useState(false)
  // Add a new state for tracking selected imported contacts
  const [selectedImportedContacts, setSelectedImportedContacts] = useState([])
  // Add state to store temporary contacts for broadcasting
  const [tempBroadcastContacts, setTempBroadcastContacts] = useState([])
  // Add state to track if we're broadcasting to imported contacts
  const [isBroadcastingImported, setIsBroadcastingImported] = useState(false)
  // Add state to store saved contacts

  // Add a new state for the change mobile number modal

  const [availableNumbers, setAvailableNo] = useState([])
  const dispatch = useDispatch()
  const dropdownRef = useRef(null)
  const importDropdownRef = useRef(null)
  const tableContainerRef = useRef(null)

  // Fetch users function - removed useCallback as requested
  const getList = ({
    currentPage = pageData.page,
    firstTime = true,
    keyword = searchQuery,
    countryCode = selectedNewNumber?.countryCode || "",
  }) => {
    let checkNo = localStorage.getItem("selectedWhatsNo")
    if (checkNo) {
      checkNo = JSON.parse(checkNo)
      countryCode = checkNo?.countryCode
    }
    if ((isLoadingMore && !firstTime) || !countryCode) return false // Prevent duplicate requests
    setIsLoading(firstTime)
    setIsLoadingMore(!firstTime)

    return new Promise((resolve, reject) => {
      dispatch(
        getAllUsersAction({
          userType: user?.userType,
          firstTime,
          data: {
            userType: "CLIENT",
            page: currentPage,
            limit: pageData.limit,
            keyword,
            countryCode,
          },
          cb: (response) => {
            // Check if there are more pages to load
            if (response && response.pagination) {
              setHasMore(response.pagination.page < response.pagination.totalPages)
            } else {
              setHasMore(false)
            }
            setIsLoading(false)
            setIsLoadingMore(false)
            setIsInitialLoad(false)
            resolve(response)
          },
          errorCb: (error) => {
            setIsLoading(false)
            setIsLoadingMore(false)
            setIsInitialLoad(false)
            reject(error)
          },
        }),
      )
    })
  }

  // Initial data load
  useEffect(() => {
    if (user && isInitialLoad && selectedNewNumber && !showChangeMobileModal) {
      setPageData({ page: 1, limit: 15 })
      getList({ currentPage: 1, firstTime: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isInitialLoad, selectedNewNumber, showChangeMobileModal])

  useEffect(() => {
    dispatch(
      getWhatsappNoRegisteredAction({
        cb: (res) => {
          setAvailableNo(res)
        },
      }),
    )
    const getNumber = localStorage.getItem("selectedWhatsNo")
    if (getNumber) {
      setSelectedNewNumber(JSON.parse(getNumber))
    } else {
      setShowChangeMobileModal(true)
    }
  }, [])

  // Debounced search function
  const delayedSearch = useRef(
    debounce((query, selectedNewNumber, showChangeMobileModal) => {
      if (!selectedNewNumber || showChangeMobileModal) {
        return false
      }
      setPageData({ page: 1, limit: 15 })
      getList({ currentPage: 1, firstTime: true, keyword: query })
    }, 500),
  ).current

  useEffect(() => {
    delayedSearch(searchQuery, selectedNewNumber, showChangeMobileModal)

    return () => {
      delayedSearch.cancel()
    }
  }, [searchQuery, delayedSearch, selectedNewNumber, showChangeMobileModal])

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
  }

  // Function to load more data for infinite scroll
  const fetchMoreData = () => {
    if (isLoadingMore || !hasMore) return

    const nextPage = pageData.page + 1
    setPageData((prev) => ({ ...prev, page: nextPage }))

    getList({
      currentPage: nextPage,
      firstTime: false,
      keyword: searchQuery,
    })
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null)
      }
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setImportDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([])
    } else {
      setSelectedContacts(contacts.map((contact) => contact.id))
    }
  }

  const handleSelectContact = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId],
    )
  }

  const handleAddContact = (e) => {
    e.preventDefault()

    // Validate form
    const newErrors = {}
    if (!newContact.name.trim()) newErrors.name = "Name is required"
    if (!newContact.number.trim()) newErrors.number = "Number is required"
    if (!/^\+?[0-9\s]{10,15}$/.test(newContact.number.replace(/\s/g, ""))) {
      newErrors.number = "Please enter a valid phone number"
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Ensure registerType is set to "contact"
    const contactToAdd = {
      ...newContact,
      registerType: "contact",
      email: newContact.email || "contact@gmail.com",
    }
    console.log("dddd", contactToAdd)

    // setNewContact({
    //   name: "",
    //   number: "",
    //   tag: "",
    //   email: "",
    //   registerType: "contact",
    // })
    setErrors({})
  }

  const handleNewContactChange = (e) => {
    const { name, value } = e.target
    setNewContact((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  // Handle Excel file import
  const handleExcelImport = (e) => {
    const file = e.target.files[0]
    if (!file) return

    setIsImporting(true)

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result)
        const workbook = XLSX.read(data, { type: "array" })

        // Assuming the first sheet contains the contacts
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet)

        // Process the data to match your contact structure
        const processedContacts = jsonData.map((row, index) => {
          // Check for email field with various possible column names
          const email = row.Email || row.email || row.EMAIL || row["E-mail"] || row["e-mail"] || "contact@gmail.com"

          // Handle phone number formatting
          let phoneNumber = row.Number || row.number || row.Phone || row.phone || row.PHONE || row.NUMBER || ""
          phoneNumber = phoneNumber.toString().trim()

          // Handle country code
          let countryCode = row.CountryCode || row["Country Code"] || row.countryCode || "+91"
          countryCode = countryCode.toString().trim()
          if (!countryCode.startsWith("+")) {
            countryCode = "+" + countryCode
          }

          return {
            id: Date.now() + index, // Generate unique IDs
            name: row.Name || row.name || row.NAME || "",
            number: phoneNumber,
            countryCode: countryCode,
            tag: row.Tag || row.tag || row.TAG || "Imported",
            source: "Excel Import",
            email: email,
            registerType: "contact",
            password: "123456", // Default password for new contacts
          }
        })

        // Filter out invalid contacts
        const validContacts = processedContacts.filter((contact) => contact.name && contact.number)

        setImportedContacts(validContacts)
        setIsImporting(false)

        // Show the preview modal instead of directly adding contacts
        if (validContacts.length > 0) {
          setShowImportPreviewModal(true)
        } else {
          showToast("Import failed", "No valid contacts found in Excel file.")
        }

        // Reset the file input
        e.target.value = null
      } catch (error) {
        console.error("Error importing Excel file:", error)
        setIsImporting(false)
        showToast("Import failed", "Failed to import contacts from Excel file.")
        e.target.value = null
      }
    }

    reader.onerror = () => {
      setIsImporting(false)
      showToast("Import failed", "Failed to read Excel file.")
      e.target.value = null
    }

    reader.readAsArrayBuffer(file)
  }

  // Function to save imported contacts
  const handleSaveImportedContacts = () => {
    if (importedContacts.length === 0) {
      showToast("No contacts", "There are no contacts to save.")
      return
    }

    // Format contacts for the API
    const formattedContacts = importedContacts.map((contact) => ({
      name: contact.name,
      email: contact.email || "contact@gmail.com",
      number: contact.number.toString().replace(/\s+/g, ""),
      countryCode: contact.countryCode || "+91",
      password: contact.password || "123456",
      registerType: "contact",
      tag: contact.tag || "Imported",
    }))

    dispatch(
      saveWhatsappContactAction({
        data: formattedContacts,
        cb: () => {
          showToast("Contacts imported", `Successfully added ${importedContacts.length} contacts.`)
          setShowImportPreviewModal(false)
          setImportedContacts([])
          setSelectedImportedContacts([])

          // Refresh the contacts list
          setPageData({ page: 1, limit: 15 })
          setIsInitialLoad(true)
        },
        errorCb: (error) => {
          console.error("Error saving contacts:", error)
          showToast("Import failed", "Failed to save contacts. Please try again.")
        },
      }),
    )
  }

  // Modify the handleTemplateSelect function to not change steps for image uploads
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)

    // Extract variables from template content
    const bodyText = template.components?.find((comp) => comp.type === "BODY")?.text || ""
    const variableMatches = [...bodyText.matchAll(/\*?\{\{(\d+)\}\}\*?/g)]

    const variables = {}
    const inputs = {}
    const autoFill = {}

    variableMatches.forEach((match) => {
      const varNum = match[1]
      if (!variables[varNum]) {
        variables[varNum] = match[0]
        inputs[varNum] = ""
        autoFill[varNum] = false
      }
    })

    setTemplateVariables(variables)
    setVariableInputs(inputs)
    setAutoFillOptions(autoFill)

    // Reset header image when selecting a new template
    setHeaderImage(null)
  }

  // Add a function to handle image upload
  const handleImageUpload = (e) => {
    if (e.target.files.length > 0) {
      setIsUploading(true) // Set uploading state to true

      const imageCallBack = (res) => {
        // Update attachments state with the uploaded files
        setAttachments(res?.imageUrls || [])

        // Set the header image to the first image URL
        if (res?.imageUrls && res.imageUrls.length > 0) {
          setHeaderImage(res.imageUrls[0])
        }

        setIsUploading(false) // Set uploading state to false when complete
        // Don't change the broadcast step - stay on the current view
      }

      const formData = new FormData()
      for (let i = 0; i < e.target.files.length; i++) {
        formData.append("images", e.target.files[i]) // Append each file to the FormData object
      }

      dispatch(uploadImageAction({ data: formData, cb: imageCallBack }))
      e.target.value = null
    }
  }

  // Modify the resetBroadcastModal function to reset image state
  const resetBroadcastModal = () => {
    setSelectedTemplate(null)
    setBroadcastStep(1)
    setTemplateVariables({})
    setVariableInputs({})
    setAutoFillOptions({})
    setHeaderImage(null)
    setIsBroadcasting(false) // Reset broadcasting state
    setShowBroadcastTemplateModal(false)
    setIsBroadcastingImported(false) // Reset imported broadcasting state
    setTempBroadcastContacts([]) // Clear temporary contacts
  }

  const handleVariableInputChange = (varNum, value) => {
    setVariableInputs((prev) => ({
      ...prev,
      [varNum]: value,
    }))
  }

  const toggleAutoFill = (varNum) => {
    setAutoFillOptions((prev) => ({
      ...prev,
      [varNum]: !prev[varNum],
    }))

    // If turning on auto-fill, set a default value based on the variable number
    if (!autoFillOptions[varNum]) {
      const autoValue = varNum === "1" ? "Contact Name" : ""
      setVariableInputs((prev) => ({
        ...prev,
        [varNum]: autoValue,
      }))
    }
  }

  // Process template content for display
  const processTemplateContent = (content) => {
    if (!content) return ""

    // Replace {{1}}, {{2}}, etc. with sample values
    // Also handle the *{{1}}* format from the API
    return content.replace(/\*?\{\{(\d+)\}\}\*?/g, (match, number) => {
      const sampleValues = {
        1: "John",
        2: "Tomorrow",
        3: "3:00 PM",
        4: "$99.99",
        5: "Product Name",
      }

      // If the match has asterisks (like *{{1}}*), keep them for formatting
      if (match.startsWith("*") && match.endsWith("*")) {
        return `*${sampleValues[number] || match}*`
      }

      return sampleValues[number] || match
    })
  }

  // Add a function to handle selecting all imported contacts
  const handleSelectAllImported = () => {
    if (selectedImportedContacts.length === importedContacts.length) {
      setSelectedImportedContacts([])
    } else {
      setSelectedImportedContacts(importedContacts.map((_, index) => index))
    }
  }

  // Add a function to handle selecting individual imported contacts
  const handleSelectImportedContact = (index) => {
    setSelectedImportedContacts((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]))
  }

  // Add a function to handle broadcast for imported contacts
  const handleBroadcastImported = () => {
    // Get the selected imported contacts
    const selectedContacts = selectedImportedContacts.map((index) => importedContacts[index])

    // Store them in the temporary broadcast contacts state
    setTempBroadcastContacts(selectedContacts)

    // Set the flag to indicate we're broadcasting to imported contacts
    setIsBroadcastingImported(true)

    // Close the import modal and open the broadcast modal
    setShowImportPreviewModal(false)
    setShowBroadcastTemplateModal(true)
  }

  // Function to export contacts to Excel
  const handleExportToExcel = () => {
    if (contacts.length === 0) {
      showToast("No contacts", "There are no contacts to export.")
      return
    }

    // Format contacts for export
    const exportData = contacts.map((contact, index) => ({
      "Sr. No.": index + 1,
      Name: contact.name,
      "Mobile Number": contact.number,
      Email: contact.email || "",
      Tag: contact.tag || "",
      Source: contact.source || "",
    }))

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData)

    // Set column widths
    const wscols = [
      { wch: 6 }, // Sr. No.
      { wch: 20 }, // Name
      { wch: 15 }, // Mobile Number
      { wch: 25 }, // Email
      { wch: 15 }, // Tag
      { wch: 15 }, // Source
    ]
    ws["!cols"] = wscols

    // Create workbook
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Contacts")

    // Generate Excel file
    XLSX.writeFile(wb, "contacts_export.xlsx")

    showToast("Export successful", `Exported ${contacts.length} contacts to Excel.`)
  }

  // Modify the handleBroadcast function to include the image
  const handleBroadcast = async () => {
    try {
      // Show the loader when starting the broadcast
      setIsBroadcasting(true)

      // Check if template requires image but no image is uploaded
      const requiresImage = selectedTemplate.components?.some(
        (comp) => comp.type === "HEADER" && comp.format === "IMAGE",
      )

      if (requiresImage && !headerImage) {
        showToast("Image Required", "Please upload a header image for this template.")
        setIsBroadcasting(false) // Hide loader on error
        return
      }

      // Check if it's a notepad template
      const isNotepadTemplate = selectedTemplate?.name?.toLowerCase().includes("notepad")

      // Prepare the request data
      let requestData = {}

      if (isNotepadTemplate) {
        // Format for notepad template
        let receipts = []

        if (isBroadcastingImported) {
          // Use temporary contacts for imported contacts
          receipts = tempBroadcastContacts.map((contact) => ({
            name: contact.name,
            number: contact.number.replace(/[\s+]/g, ""),
            email: contact.email || "contact@gmail.com",
            registerType: "contact",
            countryCode: contact.countryCode || "+91",
          }))
        } else {
          // Use regular contacts
          receipts = selectedContacts
            .map((contactId) => {
              const contact = contacts.find((c) => c.id === contactId)
              if (!contact) return null

              // Extract country code if present, otherwise use default
              let countryCode = "+91"
              if (contact.number.includes(" ")) {
                const parts = contact.number.split(" ")
                if (parts[0].startsWith("+")) {
                  countryCode = parts[0]
                }
              }

              return {
                name: contact.name,
                number: contact.number.replace(/[\s+]/g, ""),
                email: contact.email || "contact@gmail.com",
                registerType: "contact",
                countryCode: countryCode,
              }
            })
            .filter(Boolean)
        }

        // Prepare variables object
        const vars = {}
        Object.keys(variableInputs).forEach((key) => {
          vars[key] = {
            value: variableInputs[key] || false,
            useName: key === "1" && autoFillOptions[key], // Set useName to true for variable 1 if auto-fill is enabled
          }
        })

        // Ensure exact format as specified
        requestData = {
          templetename: selectedTemplate.name, // Note: using "templetename" as specified
          receipt: receipts,
          variables: vars,
        }

        // Add headerImage if available
        if (headerImage) {
          // If headerImage is a string (URL), use it directly
          if (typeof headerImage === "string") {
            requestData.headerImage = headerImage
          } else {
            // Otherwise use the first attachment if available
            if (attachments.length > 0) {
              requestData.headerImage = attachments[0]
            }
          }
        }
      } else {
        // Original format for regular templates
        // Prepare recipients array for the API
        const recipients = []

        if (isBroadcastingImported) {
          // Use temporary contacts for imported contacts
          for (const contact of tempBroadcastContacts) {
            // Format the phone number correctly (remove spaces and ensure it has country code)
            const phoneNumber = contact.number.replace(/[\s+]/g, "")

            // Create recipient object with custom components if needed
            const recipient = {
              phoneNumber: phoneNumber,
              name: contact.name,
              email: contact.email || "contact@gmail.com",
              registerType: "contact",
              countryCode: contact.countryCode || "+91",
            }

            // Handle templates with variables
            if (Object.keys(templateVariables).length > 0) {
              // Create components based on template variables
              const customComponents = []
              const parameters = Object.keys(templateVariables).map((varNum) => {
                let value = variableInputs[varNum] || ""

                // Replace with actual contact name if auto-fill is enabled
                if (autoFillOptions[varNum] && varNum === "1") {
                  value = contact.name
                }

                return {
                  type: "text",
                  text: value,
                }
              })

              if (parameters.length > 0) {
                customComponents.push({
                  type: "body",
                  parameters: parameters,
                })

                recipient.customComponents = customComponents
              }
            }

            recipients.push(recipient)
          }
        } else {
          // Use regular contacts - ensure payload structure matches imported contacts
          for (const contactId of selectedContacts) {
            const contact = contacts.find((c) => c.id === contactId)
            if (!contact) continue

            // Format the phone number correctly (remove spaces and ensure it has country code)
            const phoneNumber = contact.number.replace(/[\s+]/g, "")

            // Extract country code if present, otherwise use default
            let countryCode = "+91"
            if (contact.number.includes(" ")) {
              const parts = contact.number.split(" ")
              if (parts[0].startsWith("+")) {
                countryCode = parts[0]
              }
            }

            // Create recipient object with custom components if needed
            const recipient = {
              phoneNumber: phoneNumber,
              name: contact.name,
              email: contact.email || "contact@gmail.com",
              registerType: "contact",
              countryCode: countryCode,
            }

            // Handle templates with variables
            if (Object.keys(templateVariables).length > 0) {
              // Create components based on template variables
              const customComponents = []
              const parameters = Object.keys(templateVariables).map((varNum) => {
                let value = variableInputs[varNum] || ""

                // Replace with actual contact name if auto-fill is enabled
                if (autoFillOptions[varNum] && varNum === "1") {
                  value = contact.name
                }

                return {
                  type: "text",
                  text: value,
                }
              })

              if (parameters.length > 0) {
                customComponents.push({
                  type: "body",
                  parameters: parameters,
                })

                recipient.customComponents = customComponents
              }
            }

            recipients.push(recipient)
          }
        }

        // Prepare the request data
        requestData = {
          templateName: selectedTemplate.name,
          recipients: recipients,
          components: [], // Default empty components array
          language: selectedTemplate.language || "en_US",
        }

        // If we have variables and they're not being handled per-recipient
        if (Object.keys(templateVariables).length > 0 && !Object.values(autoFillOptions).some((val) => val)) {
          requestData.components = [
            {
              type: "body",
              parameters: Object.keys(templateVariables).map((varNum) => ({
                type: "text",
                text: variableInputs[varNum] || "",
              })),
            },
          ]
        }

        // Add header image if available
        if (headerImage) {
          // If headerImage is a string (URL), use it directly
          if (typeof headerImage === "string") {
            requestData.headerImage = headerImage
          } else {
            // Otherwise use the first attachment if available
            if (attachments.length > 0) {
              requestData.headerImage = attachments[0]
            }
          }
        }
      }

      requestData.selectedNewNumber = selectedNewNumber
      // Send the request
      dispatch(
        sendWhatsappTempletesAction({
          data: requestData,
          cb: (response) => {
            setIsBroadcasting(false) // Hide loader on success
            resetBroadcastModal()

            const successful = response.successful?.count || 0
            const failed = response.failed?.count || 0

            showToast(
              "Broadcast completed",
              `Sent "${selectedTemplate.name}" template to ${successful} contact${successful !== 1 ? "s" : ""}` +
                `.${failed > 0 ? ` Failed to send to ${failed} contact${failed !== 1 ? "s" : ""}.` : ""}`,
            )
          },
          errorCb: (error) => {
            setIsBroadcasting(false) // Hide loader on error
            console.error("Error sending template messages:", error)
            resetBroadcastModal()
            showToast(
              "Broadcast failed",
              `Error sending "${selectedTemplate?.name}" template: ${
                error.details || error.message || "Unknown error"
              }`,
            )
          },
        }),
      )
    } catch (error) {
      setIsBroadcasting(false) // Hide loader on error
      console.error("Error preparing template messages:", error)
      resetBroadcastModal()
      showToast(
        "Broadcast failed",
        `Error preparing "${selectedTemplate?.name}" template: ${error.message || "Unknown error"}`,
      )
    }
  }

  // Add a function to open the change mobile number modal
  const handleOpenChangeMobileModal = () => {
    setShowChangeMobileModal(true)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#001B3D]">Contacts</h2>
            <div className="flex items-center mt-2">
              <span className="bg-[#001B3D] bg-opacity-10 text-[#001B3D] text-sm font-medium px-3 py-1 rounded-full">
                Total Users: {userList?.pagination?.total || 0}
              </span>
            </div>

            {selectedNewNumber && (
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-sm text-gray-600">Active Number:</span>
                <div className="flex items-center gap-2">
                  <span className="bg-[#001B3D] text-white px-3 py-1 rounded-md text-sm font-medium">
                    {selectedNewNumber.NUMBER}
                  </span>
                  <button
                    onClick={() => handleOpenChangeMobileModal(true)}
                    className="text-[#001B3D] hover:text-[#002857] text-sm font-medium underline"
                  >
                    Change
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <button
              onClick={handleExportToExcel}
              className="w-full sm:w-auto px-4 py-2 bg-white border border-[#001B3D] text-[#001B3D] rounded-md hover:bg-[#f8fafc] transition-all duration-200 flex items-center justify-center shadow-sm text-sm font-medium"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Export to Excel
            </button>
            <button
              className="w-full sm:w-auto px-4 py-2 bg-[#001B3D] text-white rounded-md hover:bg-[#002857] transition-all duration-200 flex items-center justify-center shadow-sm text-sm font-medium"
              onClick={() => {
                excelFileInputRef.current?.click()
              }}
            >
              <Download className="w-4 h-4 mr-2" /> Import
              <input
                ref={excelFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelImport}
                className="hidden"
              />
            </button>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search name or mobile number"
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className={`px-3 sm:px-4 py-2.5 rounded-md transition-all duration-200 text-sm font-medium ${
                selectedContacts.length === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-[#001B3D] text-white hover:bg-[#002857] shadow-sm hover:shadow"
              }`}
              disabled={selectedContacts.length === 0}
              onClick={() => {
                if (selectedContacts.length > 0) {
                  setIsBroadcastingImported(false) // Make sure we're not in imported mode
                  setShowBroadcastTemplateModal(true)
                }
              }}
            >
              Broadcast
            </button>

            <button
              className="sm:hidden px-3 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
              onClick={() => setShowMobileFilters(true)}
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Excel Import Loader */}
        {isImporting && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-pulse">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-5 h-5 border-2 border-t-[#001B3D] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Importing contacts from Excel...</span>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center items-center p-8">
            <div className="w-6 h-6 border-2 border-t-[#001B3D] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin mr-3"></div>
            <span className="text-gray-600">Loading contacts...</span>
          </div>
        )}

        {/* Contacts Table for Desktop */}
        {!isLoading && (
          <div
            className="hidden md:block overflow-x-auto rounded-md border border-gray-200 max-h-[calc(100vh-300px)]"
            id="tableContainer"
            ref={tableContainerRef}
          >
            <InfiniteScroll
              dataLength={contacts.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <div className="flex justify-center items-center p-4">
                  <div className="w-5 h-5 border-2 border-t-[#001B3D] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin mr-2"></div>
                  <span className="text-sm text-gray-500">Loading more contacts...</span>
                </div>
              }
              scrollableTarget="tableContainer"
              endMessage={
                contacts.length > 0 && (
                  <p className="text-center text-gray-500 my-4 text-sm">
                    <b>All contacts loaded</b>
                  </p>
                )
              }
            >
              <table className="min-w-full divide-y divide-gray-200 border-collapse table-fixed">
                <thead className="bg-[#f8fafc] sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-700 w-[40px]">
                      <div className="flex items-center justify-center">
                        <div
                          onClick={handleSelectAll}
                          className={`w-4 h-4 border border-gray-300 rounded flex items-center justify-center cursor-pointer transition-colors ${
                            selectedContacts.length === contacts.length && contacts.length > 0
                              ? "bg-[#001B3D] border-[#001B3D]"
                              : "bg-white hover:bg-gray-100"
                          }`}
                        >
                          {selectedContacts.length === contacts.length && contacts.length > 0 && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                      </div>
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-700 w-[60px]">
                      Sr No
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-700 w-[200px]">
                      Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-700 w-[150px]">
                      Mobile Number
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-700 w-[200px]">
                      Email
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-700 w-[120px]">
                      Tags
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-700 w-[120px]">
                      Source
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-700 w-[80px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {contacts.length > 0 ? (
                    contacts.map((contact, index) => (
                      <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <div className="flex items-center justify-center">
                            <div
                              onClick={() => handleSelectContact(contact.id)}
                              className={`w-4 h-4 border border-gray-300 rounded flex items-center justify-center cursor-pointer transition-colors ${
                                selectedContacts.includes(contact.id)
                                  ? "bg-[#001B3D] border-[#001B3D]"
                                  : "bg-white hover:bg-gray-100"
                              }`}
                            >
                              {selectedContacts.includes(contact.id) && <Check className="w-3 h-3 text-white" />}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium text-center">
                          {index + 1}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900 truncate">
                          {contact.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 truncate">{contact.number}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 truncate">{contact.email}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span className="inline-flex items-center rounded-full bg-[#001B3D] bg-opacity-10 px-2.5 py-0.5 text-xs font-medium text-[#001B3D]">
                            {contact.tag}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 truncate">{contact.source}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <div className="relative" ref={dropdownOpen === contact.id ? dropdownRef : null}>
                            <button
                              onClick={() => setDropdownOpen(dropdownOpen === contact.id ? null : contact.id)}
                              className="text-gray-400 hover:text-gray-500 transition-colors"
                            >
                              <MoreHorizontal className="w-5 h-5" />
                            </button>

                            {dropdownOpen === contact.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 animate-[fadeIn_0.2s_ease-in-out]">
                                <div className="py-1">
                                  <button
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      setDropdownOpen(null)
                                      showToast("Edit contact", `Editing ${contact.name}'s information.`)
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      setDropdownOpen(null)
                                      showToast("Message sent", `Message sent to ${contact.name}.`)
                                    }}
                                  >
                                    Message
                                  </button>
                                  <button
                                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      setDropdownOpen(null)
                                      showToast(
                                        "Contact deleted",
                                        `${contact.name} has been removed from your contacts.`,
                                      )
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-3 py-4 text-center text-sm text-gray-500">
                        No contacts found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </InfiniteScroll>
          </div>
        )}

        {/* Contacts Cards for Mobile - Using the reusable ContactCard component with InfiniteScroll */}
        {!isLoading && (
          <div className="md:hidden">
            <InfiniteScroll
              dataLength={contacts.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <div className="flex justify-center items-center p-4">
                  <div className="w-5 h-5 border-2 border-t-[#001B3D] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin mr-2"></div>
                  <span className="text-sm text-gray-500">Loading more contacts...</span>
                </div>
              }
              endMessage={
                contacts.length > 0 && (
                  <p className="text-center text-gray-500 my-4 text-sm">
                    <b>All contacts loaded</b>
                  </p>
                )
              }
            >
              <div className="space-y-3">
                {contacts.length > 0 ? (
                  contacts.map((contact, index) => (
                    <ContactCard
                      dropdownOpen={dropdownOpen}
                      setDropdownOpen={setDropdownOpen}
                      selectedContacts={selectedContacts}
                      handleSelectContact={handleSelectContact}
                      key={contact.id}
                      contact={contact}
                      isMobile={true}
                      index={index}
                    />
                  ))
                ) : (
                  <div className="text-center p-4 text-gray-500">No contacts found</div>
                )}
              </div>
            </InfiniteScroll>
          </div>
        )}
      </div>

      {/* Render the modals component */}
      <ContactModals
        showAddContactModal={showAddContactModal}
        setShowAddContactModal={setShowAddContactModal}
        newContact={newContact}
        setNewContact={setNewContact}
        errors={errors}
        setErrors={setErrors}
        handleNewContactChange={handleNewContactChange}
        handleAddContact={handleAddContact}
        showMobileFilters={showMobileFilters}
        setShowMobileFilters={setShowMobileFilters}
        showBroadcastTemplateModal={showBroadcastTemplateModal}
        broadcastStep={broadcastStep}
        setBroadcastStep={setBroadcastStep}
        selectedTemplate={selectedTemplate}
        templeteList={templeteList}
        handleTemplateSelect={handleTemplateSelect}
        processTemplateContent={processTemplateContent}
        headerImage={headerImage}
        setHeaderImage={setHeaderImage}
        isUploading={isUploading}
        fileInputRef={fileInputRef}
        handleImageUpload={handleImageUpload}
        templateVariables={templateVariables}
        variableInputs={variableInputs}
        autoFillOptions={autoFillOptions}
        handleVariableInputChange={handleVariableInputChange}
        toggleAutoFill={toggleAutoFill}
        handleBroadcast={handleBroadcast}
        resetBroadcastModal={resetBroadcastModal}
        isBroadcasting={isBroadcasting}
        showImportPreviewModal={showImportPreviewModal}
        setShowImportPreviewModal={setShowImportPreviewModal}
        importedContacts={importedContacts}
        selectedImportedContacts={selectedImportedContacts}
        setSelectedImportedContacts={setSelectedImportedContacts}
        handleSelectAllImported={handleSelectAllImported}
        handleSelectImportedContact={handleSelectImportedContact}
        handleSaveImportedContacts={handleSaveImportedContacts}
        handleBroadcastImported={handleBroadcastImported}
        isBroadcastingImported={isBroadcastingImported}
        tempBroadcastContacts={tempBroadcastContacts}
        contacts={contacts}
        showToast={showToast}
        selectedContacts={selectedContacts}
      />

      {/* Change Mobile Number Modal */}
      {showChangeMobileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Select Mobile Number</h3>
              <button
                onClick={() => setShowChangeMobileModal(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Choose a WhatsApp number to use:</p>
              <div className="grid gap-2 max-h-[300px] overflow-y-auto">
                {availableNumbers.length > 0 ? (
                  availableNumbers.map((num) => (
                    <div
                      key={num.id}
                      onClick={() => {
                        setSelectedNewNumber(num)
                      }}
                      className={`p-3 border rounded-md cursor-pointer flex items-center justify-between ${
                        selectedNewNumber?.NUMBER === num.NUMBER
                          ? "border-[#001B3D] bg-[#001B3D] bg-opacity-5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="font-medium">{num.NUMBER}</div>
                        {num.NUMBER === selectedNewNumber?.NUMBER && (
                          <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                            Selected
                          </span>
                        )}
                      </div>
                      {selectedNewNumber?.NUMBER === num.NUMBER && <Check className="w-5 h-5 text-[#001B3D]" />}
                    </div>
                  ))
                ) : (
                  <div className="text-center p-4 text-gray-500">No WhatsApp numbers available</div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowChangeMobileModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={!selectedNewNumber?.NUMBER}
                onClick={() => {
                  localStorage.setItem("selectedWhatsNo", JSON.stringify(selectedNewNumber))
                  setPageData({ page: 1, limit: 15 })
                  getList({ currentPage: 1, firstTime: true, countryCode: selectedNewNumber?.countryCode })
                  setShowChangeMobileModal(false)
                  getTemplete()
                }}
                className={`px-4 py-2 rounded-md transition-all ${
                  !selectedNewNumber?.NUMBER
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-[#001B3D] text-white hover:bg-[#002857]"
                }`}
              >
                Update Number
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 md:hidden p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Filters</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input type="checkbox" id="tag-lead" className="h-4 w-4 text-[#001B3D] border-gray-300 rounded" />
                    <label htmlFor="tag-lead" className="ml-2 text-sm text-gray-700">
                      CTWA Lead
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" id="tag-client" className="h-4 w-4 text-[#001B3D] border-gray-300 rounded" />
                    <label htmlFor="tag-client" className="ml-2 text-sm text-gray-700">
                      Client
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="tag-imported"
                      className="h-4 w-4 text-[#001B3D] border-gray-300 rounded"
                    />
                    <label htmlFor="tag-imported" className="ml-2 text-sm text-gray-700">
                      Imported
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input type="checkbox" id="source-ad" className="h-4 w-4 text-[#001B3D] border-gray-300 rounded" />
                    <label htmlFor="source-ad" className="ml-2 text-sm text-gray-700">
                      AD
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="source-excel"
                      className="h-4 w-4 text-[#001B3D] border-gray-300 rounded"
                    />
                    <label htmlFor="source-excel" className="ml-2 text-sm text-gray-700">
                      Excel Import
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="source-manual"
                      className="h-4 w-4 text-[#001B3D] border-gray-300 rounded"
                    />
                    <label htmlFor="source-manual" className="ml-2 text-sm text-gray-700">
                      Manual Entry
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowMobileFilters(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Apply filters logic would go here
                  setShowMobileFilters(false)
                }}
                className="px-4 py-2 bg-[#001B3D] text-white rounded-md hover:bg-[#002857] transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

